// Copyright (c) Docugami, Inc. All rights reserved.

import { ChunkInstance } from "networking/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ModalSelectionItemType } from "store/documents/types";
import { TagEditorEditCancelledSchema, TagEditorEditSchema, TagEditorSaveSchema, TagEditorTagSelectedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { ChunkResizing } from "utils/ChunkResizing";
import { isValidDecodedTag, getValidatedDecodedTagErrorMessage } from "utils/TagStandardUtils";
import { useKeyPressHandler } from "./KeyPressHandler";
import { useLabelChunk } from "./LabelChunk";
import { useModalSelectionActions, useModalSelectionListener } from "./ModalSelection";
import { useAppSelector } from "./redux";
import { DgmlInstancesSingleton } from "./documentDgml";
import { selectLabelState } from "store/frontend/selectors";

type TagType = "Recent" | "Suggested";

export interface ITagAccept {
    label: string;
    instance: ChunkInstance;
}

export interface ITagText {
    isEditing: boolean;
    targetTags: string[];
    recentTags: string[];
    tagText: string;
    isValid: boolean;
    setTagText: (input: string) => void;
    accept: () => Promise<ITagAccept>;
    undo: () => void;
    onEdit: () => void;
    selectTag: (label: string, type: TagType) => void;
    removeTag: () => void;
    documentId: string;
    clusterId: string;
    chunkInstance?: ChunkInstance;
    errorMessage?: string;
}

export function useTagText(clusterId: string, documentId: string, chunkInstance?: ChunkInstance): ITagText {
    const [tagText, setTagText] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [recommendedTags, setRecommendedTags] = useState<string[]>([]);
    const recentLabels = useAppSelector(state => state.sdv.recentLabels?.[clusterId] ?? []);
    const { labelChunk, getRecommendedTags, getChunkLabel } = useLabelChunk(clusterId, documentId);
    const { isActive: isChunkModalActive } = useModalSelectionListener([ModalSelectionItemType.Chunk]);
    const { confirm: confirmModalAction } = useModalSelectionActions();
    const editTokenRange = useAppSelector(state => state.sdv.document?.edit?.tokenRange);
    const { data: labelState } = useAppSelector(selectLabelState(clusterId));

    useKeyPressHandler(isEditing ? 'Escape' : undefined, () => {
        undo();
    });

    const chunkLabel = useMemo(() => {
        return labelState?.entities[chunkInstance?.labelId ?? '']?.label
    }, [labelState?.entities, chunkInstance?.labelId])

    const targetTags = useMemo(() => {
        const promotedTag = recommendedTags.find(tag => tag === tagText);
        return !!promotedTag ? recommendedTags.filter(tag => tag !== tagText) : recommendedTags;
    }, [recommendedTags, tagText]);

    const isValid: boolean = useMemo(() => {
        const targetLabel = tagText.trim();
        if (!targetLabel && !!chunkLabel && !!chunkInstance?.chunkInstanceId) {
            return true;
        }
        return isValidDecodedTag(targetLabel);
    }, [chunkLabel, chunkInstance, tagText]);

    useEffect(() => {
        if (!!chunkInstance) {
            setRecommendedTags(getRecommendedTags(chunkInstance).map(label => {
                return label.value;
            }));
        }
        else {
            setRecommendedTags([]);
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [chunkInstance]);

    const updateTagText = useCallback(() => {
        let targetText = '';
        // The selected UX instance can become stale so we fetch it from ID
        if (!!chunkInstance) {
            targetText = getChunkLabel(chunkInstance);
        }

        setTagText(targetText);
    }, [chunkInstance, getChunkLabel]);

    useEffect(() => {
        setIsEditing(false);
        updateTagText()
    },
        [updateTagText]);

    const onEdit = useCallback(() => {
        if (!chunkInstance) {
            return;
        }
        Telemetry.trackEvent<TagEditorEditSchema>({
            eventName: TelemetryEvent.TagEditorEdit
        });

        setIsEditing(true);
    }, [chunkInstance]);

    const removeTag = useCallback(async (): Promise<ITagAccept> => {
        if (!chunkInstance) {
            throw new Error("Cannot remove the tag for non-existent chunk");
        }

        const targetLabel = tagText.trim();
        let targetChunk = chunkInstance;
        if (!!targetChunk.chunk?.labels.length) {
            targetChunk = await DgmlInstancesSingleton.removeLabel({
                docsetId: targetChunk.chunk!.docsetId,
                fileId: targetChunk.chunk!.fileId,
                spanId: targetChunk.chunk!.spanId
            });
        }

        setIsEditing(false);
        return { label: targetLabel, instance: targetChunk };
    }, [tagText, chunkInstance]);

    const accept = useCallback(async (): Promise<ITagAccept> => {
        if (!chunkInstance) {
            throw new Error("Cannot accept the tag for non-existent chunk");
        }

        const targetLabel = tagText.trim();
        let targetChunk = chunkInstance;
        if (!targetLabel && !!targetChunk.chunk?.labels.length) {
            return await removeTag();
        }
        else if (!isValidDecodedTag(targetLabel)) {
            throw new Error(`Invalid tag text: ${targetLabel}`);
        }
        else if (!!targetLabel) {
            if (!!editTokenRange) {
                // Resize
                targetChunk = await ChunkResizing.commitResize(chunkInstance, editTokenRange);
            }

            targetChunk = await labelChunk(targetChunk, targetLabel);

            if (isChunkModalActive) {
                confirmModalAction();
            }

            Telemetry.trackEvent<TagEditorSaveSchema>({
                eventName: TelemetryEvent.TagEditorSave
            });
        }

        setIsEditing(false);
        return { label: targetLabel, instance: targetChunk };
    }, [tagText, confirmModalAction, chunkInstance, isChunkModalActive, editTokenRange, labelChunk, removeTag]);

    const undo = useCallback(() => {
        Telemetry.trackEvent<TagEditorEditCancelledSchema>({
            eventName: TelemetryEvent.TagEditorEditCancelled
        });
        setIsEditing(false);
        updateTagText();
    }, [updateTagText]);

    const selectTag = useCallback((label: string, selectionType: string) => {
        Telemetry.trackEvent<TagEditorTagSelectedSchema>({
            label: label,
            type: selectionType,
            eventName: TelemetryEvent.TagEditorTagSelected
        });
        setIsEditing(true);
        setTagText(label);
    }, []);

    const errorMessage = useMemo(() => {
        if (!isValid) {
            return getValidatedDecodedTagErrorMessage(tagText.trim()) || undefined;
        }
    }, [isValid, tagText]);

    return {
        isEditing: isEditing,
        targetTags: targetTags,
        tagText: tagText,
        isValid: isValid,
        setTagText: setTagText,
        onEdit: onEdit,
        accept: accept,
        undo: undo,
        selectTag: selectTag,
        removeTag: removeTag,
        documentId: documentId,
        clusterId: clusterId,
        recentTags: recentLabels,
        chunkInstance: chunkInstance,
        errorMessage: errorMessage
    }
}
